import React, { useEffect, useState } from 'react';
import { IonPage, IonContent, IonRefresher, IonRefresherContent, useIonRouter } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '../../store/authStore';
import { useListsStore } from '../../store/listsStore';
import { useTasksStore } from '../../store/tasksStore';
import { useCalendarStore } from '../../store/calendarStore';
import { useSpecialDaysStore } from '../../store/specialDaysStore';
import { useHomeLayoutStore } from '../../store/homeLayoutStore';
import { useNotesStore } from '../../store/notesStore';
import { useBinsStore } from '../../store/binsStore';
import { useRemindersStore } from '../../store/remindersStore';
import { useChatStore } from '../../store/chatStore';
import { useKitchenStore } from '../../store/kitchenStore';
import { useBudgetStore } from '../../store/budgetStore';
import { useDocumentsStore } from '../../store/documentsStore';
import { useLocatorStore } from '../../store/locatorStore';
import { format, isToday, isTomorrow, parseISO, addDays, differenceInDays, startOfWeek, isAfter } from 'date-fns';
import Icon from '../../components/Icon';
import { initials } from '../../utils/constants';
import { fetchExchangeRates } from '../../utils/currency';
import { fetchWeather, WeatherData } from '../../utils/weather';
import NoticeBoardWidget from '../../plugins/NoticeBoardWidgetPlugin';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

// Hook to detect desktop viewport
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
};

const HomePage: React.FC = () => {
  const { user, family } = useAuthStore();
  const { lists, fetchLists } = useListsStore();
  const { tasks, fetchTasks } = useTasksStore();
  const { events, fetchEvents } = useCalendarStore();
  const { upcoming, fetchUpcoming } = useSpecialDaysStore();
  const { notes, fetchNotes } = useNotesStore();
  const { bins, fetchBins } = useBinsStore();
  const { upcoming: upcomingReminders, fetchUpcoming: fetchUpcomingReminders } = useRemindersStore();
  const { mealPlans, fetchMealPlans } = useKitchenStore();
  const { tiles, maxVisible } = useHomeLayoutStore();
  const { transactions, fetchTransactions } = useBudgetStore();
  const { documents, fetchDocuments } = useDocumentsStore();
  const { locations, fetchFamilyLocations } = useLocatorStore();
  const history = useHistory();
  const ionRouter = useIonRouter();
  const [now, setNow] = useState(new Date());
  const [fabOpen, setFabOpen] = useState(false);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const isAdmin = family?.role === 'admin';
  const isDesktop = useIsDesktop();

  // Core data — runs once per family and doesn't care about viewport.
  useEffect(() => {
    if (!family) return;
    fetchLists();
    fetchTasks();
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    const tomorrowStr = format(addDays(new Date(), 1), 'yyyy-MM-dd');
    fetchEvents(todayStr, tomorrowStr);
    fetchUpcoming();
    fetchNotes();
    fetchBins();
    fetchUpcomingReminders();
    fetchMealPlans(todayStr).catch(() => {});
    fetchExchangeRates();
  }, [family, fetchLists, fetchTasks, fetchEvents, fetchUpcoming, fetchNotes, fetchBins, fetchUpcomingReminders, fetchMealPlans]);

  // Desktop-only widgets — refetch if the viewport crosses the breakpoint.
  useEffect(() => {
    if (!family || !isDesktop) return;
    fetchTransactions();
    fetchDocuments();
    fetchFamilyLocations();
    fetchWeather().then(w => { if (w) setWeather(w); });
  }, [family, isDesktop, fetchTransactions, fetchDocuments, fetchFamilyLocations]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = async (event: any) => {
    if (family) {
      const todayStr = format(new Date(), 'yyyy-MM-dd');
      const tomorrowStr = format(addDays(new Date(), 1), 'yyyy-MM-dd');
      await Promise.all([fetchLists(), fetchTasks(), fetchEvents(todayStr, tomorrowStr), fetchUpcoming(), fetchNotes()]);
    }
    event.detail.complete();
  };

  const todayEvents = events.filter(e => { try { return isToday(parseISO(e.start_date)); } catch { return false; } });
  const tomorrowEvents = events.filter(e => { try { return isTomorrow(parseISO(e.start_date)); } catch { return false; } });
  const pendingTasks = tasks.filter(t => t.status !== 'done' && t.status !== 'completed');
  const completedCount = tasks.filter(t => t.status === 'done' || t.status === 'completed').length;
  const upcomingBirthdays = upcoming.filter(s => {
    try { const d = differenceInDays(parseISO(s.date), new Date()); return d >= 0 && d <= 7; } catch { return false; }
  });
  const pinnedNotes = notes.filter(n => n.pinned);
  const firstName = user?.displayName?.split(' ')[0] || 'there';
  const allVisibleTiles = tiles.filter(t => t.visible && (isAdmin || t.id !== 'budget'));
  // "Show fewer tiles" setting — cap the list but always keep quick-actions.
  const visibleTiles = maxVisible && maxVisible > 0
    ? (() => {
        const quick = allVisibleTiles.filter(t => t.category === 'quickAction');
        const main = allVisibleTiles.filter(t => t.category !== 'quickAction');
        const remaining = Math.max(0, maxVisible - quick.length);
        return [...quick, ...main.slice(0, remaining)];
      })()
    : allVisibleTiles;

  // Tasks due today
  const tasksDueToday = pendingTasks.filter(t => {
    try { return t.due_date && isToday(parseISO(t.due_date)); } catch { return false; }
  });

  // Today-only task progress — the dashboard widget should reflect what
  // actually needs doing today, not every task ever logged. Includes
  // completed-today tasks so the bar fills as the day progresses.
  const tasksDoneToday = tasks.filter(t => {
    if (t.status !== 'done' && t.status !== 'completed') return false;
    // Prefer the completion time if we have it; otherwise fall back to due_date.
    const stamp = t.completed_at || t.due_date;
    if (!stamp) return false;
    try { return isToday(parseISO(stamp)); } catch { return false; }
  });
  const todayProgressTotal = tasksDueToday.length + tasksDoneToday.length;
  const todayProgressDone = tasksDoneToday.length;
  const todayProgressPct = todayProgressTotal > 0
    ? Math.min(100, Math.round((todayProgressDone / todayProgressTotal) * 100))
    : 0;

  // Bins due today or tomorrow
  const binsDueSoon = (bins || []).filter(b => {
    if (!b.enabled || !b.next_collection) return false;
    try {
      const d = differenceInDays(parseISO(b.next_collection), new Date());
      return d >= 0 && d <= 1;
    } catch { return false; }
  });

  // Build notice board items
  let notices: { icon: string; color: string; text: string; action?: string }[] = [];
  try {
    // Chip format per dashboard-desktop.pdf: "Title · today" or "Title · N days".
    if (todayEvents.length > 0) {
      todayEvents.slice(0, 2).forEach(e => {
        const time = e.start_time ? String(e.start_time).slice(0, 5) : '';
        notices.push({ icon: 'event', color: '#b39ddb', text: time ? `${e.title} · ${time}` : `${e.title} · today`, action: '/app/calendar' });
      });
    }
    tasksDueToday.forEach(t => {
      notices.push({ icon: 'check_circle', color: '#81c784', text: `${t.title} · today`, action: '/app/tasks' });
    });
    binsDueSoon.forEach(b => {
      try {
        const isToday_ = b.next_collection ? differenceInDays(parseISO(b.next_collection), new Date()) === 0 : false;
        notices.push({ icon: 'delete', color: b.colour || '#78909c', text: `${b.bin_type} bin · ${isToday_ ? 'today' : 'tomorrow'}` });
      } catch { /* skip */ }
    });
    if (tomorrowEvents.length > 0) {
      const e = tomorrowEvents[0];
      notices.push({ icon: 'event_upcoming', color: '#64b5f6', text: `${e.title} · tomorrow${tomorrowEvents.length > 1 ? ` +${tomorrowEvents.length - 1}` : ''}`, action: '/app/calendar' });
    }
    upcomingBirthdays.forEach(s => {
      try {
        const d = differenceInDays(parseISO(s.date), new Date());
        const tail = d === 0 ? 'today' : d === 1 ? 'tomorrow' : `${d} days`;
        notices.push({ icon: 'cake', color: '#ff8a65', text: `${s.title} · ${tail}`, action: '/app/special-days' });
      } catch { /* skip */ }
    });

    // Upcoming reminders
    upcomingReminders.forEach(r => {
      try {
        const d = r.next_occurrence ? differenceInDays(parseISO(r.next_occurrence.slice(0, 10)), new Date()) : -1;
        if (d >= 0 && d <= 7) {
          const tail = d === 0 ? 'today' : d === 1 ? 'tomorrow' : `${d} days`;
          notices.push({ icon: r.icon || 'notifications', color: r.colour || '#ffb74d', text: `${r.title} · ${tail}`, action: '/app/reminders' });
        }
      } catch { /* skip */ }
    });
  } catch { /* notice board data error */ }

  // Up Next event (next upcoming event — today or tomorrow)
  const upNextEvent = [...todayEvents, ...tomorrowEvents]
    .filter(e => {
      if (!e.start_time) return true;
      try {
        const eventDateTime = parseISO(`${e.start_date}T${e.start_time}`);
        return isAfter(eventDateTime, new Date()) || isTomorrow(parseISO(e.start_date));
      } catch { return true; }
    })
    .sort((a, b) => {
      const dateA = `${a.start_date}T${a.start_time || '00:00'}`;
      const dateB = `${b.start_date}T${b.start_time || '00:00'}`;
      return dateA.localeCompare(dateB);
    })[0] || null;

  // Desktop tile groupings
  const quickActionTiles = visibleTiles.filter(t => t.category === 'quickAction');
  const mainTiles = visibleTiles.filter(t => t.category === 'main');

  // Live-data subtitles for quick action tiles (matches v2 PDF)
  const quickActionSubtitle = (id: string): string | undefined => {
    switch (id) {
      case 'calendar': {
        if (todayEvents.length === 0 && tomorrowEvents.length === 0) return 'Nothing on';
        const parts: string[] = [];
        if (todayEvents.length > 0) parts.push(`${todayEvents.length} event${todayEvents.length === 1 ? '' : 's'} today`);
        if (tomorrowEvents.length > 0) parts.push(`${tomorrowEvents.length} tomorrow`);
        return parts.join(', ');
      }
      case 'chat': {
        // dashboard-desktop.pdf: "Ayo started a thread". We use the last
        // sender's first name + a light verb based on their last message.
        const lastMsg = useChatStore.getState().messages?.slice(-1)[0];
        if (!lastMsg) return 'Quiet in here';
        const who = lastMsg.sender_display_name?.split(' ')[0] || 'Someone';
        return `${who} ${lastMsg.media_url ? 'sent a photo' : 'started a thread'}`;
      }
      case 'lists': {
        if (lists.length === 0) return 'No lists yet';
        const totalPending = lists.reduce((sum, l) => sum + (Number(l.pending_count) || 0), 0);
        const firstList = lists[0];
        return `${totalPending} item${totalPending === 1 ? '' : 's'}${firstList ? ` · ${firstList.name}` : ''}`;
      }
      case 'kitchen': {
        // dashboard-desktop.pdf: "Jollof rice · 20 min" — tonight's planned
        // dinner + cook-time hint if we have a recipe.
        const { mealPlans } = useKitchenStore.getState();
        const todayKey = format(new Date(), 'yyyy-MM-dd');
        const tonight = (mealPlans || []).find(mp => mp.date === todayKey && mp.meal_type === 'dinner');
        if (!tonight) return 'No dinner planned';
        const name = tonight.recipe_title || tonight.custom_meal || 'Dinner';
        const time = (tonight as any).prep_time_mins ?? (tonight as any).total_time_mins;
        return time ? `${name} · ${time} min` : name;
      }
      default:
        return undefined;
    }
  };

  // Today at a glance data
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weeklyExpenses = transactions
    .filter(t => {
      try { return t.transaction_type === 'expense' && isAfter(parseISO(t.date), weekStart); } catch { return false; }
    })
    .reduce((sum, t) => sum + (t.amount_pence || 0), 0);

  const recentDocs = documents.filter(d => {
    try { return differenceInDays(new Date(), parseISO(d.updated_at)) <= 7; } catch { return false; }
  });

  // iOS widget sync — payload feeds both the legacy notice board and the
  // widget-system redesign (small/medium/large + tones).
  useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      try {
        // Build chronologically ordered pins from today's events + tasks + bins.
        const FALLBACK = ['#c5512d', '#6f8a86', '#8ab07e', '#a06bb8'];
        type RawPin = { sortKey: string; time: string; title: string; color: string; ownerInitial: string };
        const rawPins: RawPin[] = [];

        todayEvents.forEach((e, i) => {
          const timeLabel = e.start_time ? String(e.start_time).slice(0, 5) : 'All day';
          rawPins.push({
            sortKey: e.start_time ? String(e.start_time).slice(0, 5) : '00:00',
            time: timeLabel,
            title: e.title,
            color: e.colour || FALLBACK[i % FALLBACK.length],
            ownerInitial: (e.title || '·').trim().charAt(0).toUpperCase(),
          });
        });
        tasksDueToday.forEach((t, i) => {
          rawPins.push({
            sortKey: '99:59',
            time: 'Today',
            title: t.title,
            color: FALLBACK[i % FALLBACK.length],
            ownerInitial: (t.title || '·').trim().charAt(0).toUpperCase(),
          });
        });
        binsDueSoon.forEach(b => {
          try {
            const isToday_ = b.next_collection ? differenceInDays(parseISO(b.next_collection), new Date()) === 0 : false;
            if (!isToday_) return;
            rawPins.push({
              sortKey: '07:00',
              time: 'Now',
              title: `${b.bin_type} bin — kerb`,
              color: b.colour || '#c5512d',
              ownerInitial: '·',
            });
          } catch { /* skip */ }
        });
        rawPins.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
        const pins = rawPins.slice(0, 3).map(({ sortKey, ...rest }) => rest);

        // Next event pill — earliest future pin, with a relative "in Nm" label.
        const nowTime = format(now, 'HH:mm');
        const upcoming = rawPins.find(p => p.sortKey >= nowTime && p.time !== 'Now');
        let nextEvent: { title: string; inLabel: string; color?: string } | null = null;
        if (upcoming) {
          const [hh, mm] = upcoming.sortKey.split(':').map(Number);
          const target = new Date(now);
          target.setHours(hh || 0, mm || 0, 0, 0);
          const diffMins = Math.max(Math.round((target.getTime() - now.getTime()) / 60000), 0);
          let inLabel = 'Now';
          if (diffMins > 0) {
            if (diffMins < 60) inLabel = `in ${diffMins}m`;
            else {
              const h = Math.floor(diffMins / 60);
              const m = diffMins % 60;
              inLabel = m === 0 ? `in ${h}h` : `in ${h}h ${m}m`;
            }
          }
          nextEvent = { title: upcoming.title, inLabel, color: upcoming.color };
        }

        const data = {
          greeting: getGreeting(),
          firstName,
          dateString: format(now, 'EEEE, d MMMM yyyy'),
          notices: notices.slice(0, 5).map(n => ({ icon: n.icon, color: n.color, text: n.text })),
          updatedAt: new Date().toISOString(),
          clockTime: format(now, 'HH:mm'),
          timezoneLabel: 'London',
          completedCount,
          totalCount: tasks.length,
          nextEvent,
          pins,
          tone: 'ink' as const,
        };
        NoticeBoardWidget.updateWidgetData({ payload: JSON.stringify(data) });
      } catch { /* non-critical */ }
    }
  }, [notices.length, firstName, now, todayEvents, tasksDueToday, binsDueSoon, completedCount, tasks.length]);

  // ─── FAB (shared between mobile & desktop) ───
  const renderFab = () => (
    family ? (
      <>
        {fabOpen && (
          <div
            onClick={() => setFabOpen(false)}
            onTouchMove={e => e.preventDefault()}
            style={{
              position: 'fixed', inset: 0, zIndex: 90,
              background: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
              touchAction: 'none', overscrollBehavior: 'none',
            }}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setFabOpen(false); }}
              aria-label="Close quick actions menu"
              style={{
                position: 'absolute', top: 16, right: 16,
                width: 44, height: 44, borderRadius: '50%',
                background: 'var(--fh-overlay-strong)', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <Icon name="close" size={24} color="var(--fh-dark-text)" />
            </button>
          </div>
        )}

        {fabOpen && (
          <div style={{
            position: 'fixed', bottom: isDesktop ? 104 : 'calc(var(--fh-fab-bottom) + 72px)',
            right: isDesktop ? 40 : 'max(20px, calc(50% - 450px + 20px))', zIndex: 95,
            display: 'flex', flexDirection: 'column-reverse', gap: 10,
          }}>
            {[
              { icon: 'add_shopping_cart', label: 'New list', color: '#e9c46a', path: '/app/lists?create=1' },
              { icon: 'check_circle', label: 'Add task', color: '#81c784', path: '/app/tasks?create=1' },
              { icon: 'calendar_month', label: 'Add event', color: '#b39ddb', path: '/app/calendar?create=1' },
              { icon: 'chat_bubble', label: 'Chat', color: '#80cbc4', path: '/app/chat' },
            ].map((action, i) => (
              <button
                key={action.label}
                aria-label={action.label}
                onClick={() => { setFabOpen(false); ionRouter.push(action.path, 'forward', 'push'); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'var(--fh-dark-card)', border: 'none',
                  borderRadius: 12, padding: '10px 16px 10px 12px',
                  cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  animation: `fh-fab-pop 0.2s ease-out ${i * 0.05}s both`,
                  willChange: 'transform',
                }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${action.color}18`,
                }}>
                  <Icon name={action.icon} size={20} color={action.color} />
                </div>
                <span style={{ color: 'var(--fh-dark-text)', fontSize: 14, fontWeight: 600 }}>{action.label}</span>
              </button>
            ))}
          </div>
        )}

        <button
          aria-label={fabOpen ? 'Close quick actions' : 'Open quick actions'}
          onClick={() => setFabOpen(!fabOpen)}
          className="fh-fab-button"
          style={{
            position: 'fixed',
            bottom: isDesktop ? 24 : 'var(--fh-fab-bottom)',
            right: isDesktop ? 24 : 'max(20px, calc(50% - 450px + 20px))',
            zIndex: 95,
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--fh-primary), var(--fh-primary-light))',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 24px rgba(164,57,23,0.45), 0 0 0 5px rgba(164,57,23,0.12)',
            transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
            transform: fabOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          <Icon name="add" size={26} color="#fff" />
        </button>
      </>
    ) : null
  );

  // Helpers used by both desktop and mobile v2 renders.
  const relLabel = (dateStr: string, timeStr: string | null | undefined) => {
    try {
      const iso = `${dateStr}T${(timeStr || '00:00').slice(0, 5)}`;
      const then = parseISO(iso);
      const mins = Math.round((then.getTime() - now.getTime()) / 60000);
      if (mins <= 0) return 'Now';
      if (mins < 60) return `in ${mins}m`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m === 0 ? `in ${h}h` : `in ${h}h ${m}m`;
    } catch { return ''; }
  };

  const railEvents = [...todayEvents]
    .filter(e => {
      if (!e.start_time) return false;
      try {
        return isAfter(parseISO(`${e.start_date}T${e.start_time}`), now);
      } catch { return false; }
    })
    .sort((a, b) => `${a.start_date}T${a.start_time || ''}`.localeCompare(`${b.start_date}T${b.start_time || ''}`))
    .slice(0, 3);

  const progressTotal = tasks.length;
  const progressDone = completedCount;
  const progressPct = progressTotal > 0 ? Math.min(100, Math.round((progressDone / progressTotal) * 100)) : 0;
  const heroSub = upNextEvent
    ? `Next up: ${upNextEvent.title}${upNextEvent.start_time ? ` at ${String(upNextEvent.start_time).slice(0, 5)}` : ''}.`
    : 'Nothing on the calendar right now — breathe.';

  // Chip items — pulled from today's key notices.
  const chipItems = notices.slice(0, 3).map(n => ({
    text: n.text,
    color: n.color,
    action: n.action,
  }));

  // ─── DESKTOP DASHBOARD ───
  if (isDesktop) {
    const leftToFinish = Math.max(progressTotal - progressDone, 0);
    return (
      <IonPage>
        <IonContent fullscreen className="fh-mobile-content">
          <div className="fhv2-desktop">
            {/* Editorial masthead — prototype-style */}
            <div className="fhv2-masthead">
              <div className="fhv2-masthead-eyebrow">
                {format(now, 'EEEE · d MMMM yyyy').toUpperCase()} · {family?.members?.length || 1} AT HOME
              </div>
              <h1 className="fhv2-masthead-title">
                {getGreeting()}, <em>{firstName}.</em>
              </h1>
              <div className="fhv2-masthead-lead">
                {progressTotal === 0
                  ? <>A quiet one — <em>nothing queued</em>.</>
                  : leftToFinish === 0
                    ? <>All caught up — <em>nice one</em>.</>
                    : <>{leftToFinish} thing{leftToFinish === 1 ? '' : 's'} still to do{upNextEvent ? <>, and <em>{upNextEvent.title}</em> {relLabel(upNextEvent.start_date, upNextEvent.start_time)}</> : <>. <em>Take the morning slow</em></>}.</>
                }
              </div>
              <div className="fhv2-masthead-clock">
                <div className="fhv2-masthead-time">{format(now, localStorage.getItem('fh-clock-format') === '12h' ? 'h:mm a' : 'HH:mm')}</div>
                <div className="fhv2-masthead-tz">EUROPE · LONDON</div>
              </div>
            </div>

            <div className="fhv2-grid-main">
              <div>
                {/* Hero card */}
                <div className="fhv2-hero">
                  <div className="fhv2-hero-label">Today at a glance</div>
                  <h3>
                    {progressTotal === 0
                      ? 'A quiet one — nothing queued.'
                      : `${progressTotal} thing${progressTotal === 1 ? '' : 's'} to finish, ${leftToFinish} still to do.`}
                  </h3>
                  <div className="fhv2-hero-sub">{heroSub}</div>
                  {progressTotal > 0 && (
                    <>
                      <div className="fhv2-progress-row">
                        <span>Household progress</span>
                        <span><b>{progressDone} of {progressTotal}</b> done</span>
                      </div>
                      <div className="fhv2-progress-track">
                        <div style={{ width: `${progressPct}%` }} />
                      </div>
                    </>
                  )}
                  {chipItems.length > 0 && (
                    <div className="fhv2-chip-row">
                      {chipItems.map((c, i) => (
                        <button
                          key={i}
                          className="fhv2-chip fhv2-unset"
                          onClick={() => c.action && history.push(c.action)}
                          style={{ cursor: c.action ? 'pointer' : 'default' }}
                        >
                          <span className="fhv2-chip-dot" style={{ background: c.color }} />
                          {c.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Primary actions — 4-up */}
                <div className="fhv2-section-head">
                  <h4>Every day.</h4>
                  <span>The four you open most.</span>
                </div>
                <div className="fhv2-actions">
                  {quickActionTiles.map((tile, idx) => {
                    const tone = ['', 'tone-sage', 'tone-forest', 'tone-warn'][idx] || '';
                    const badge = tile.id === 'chat' ? '3' : null;
                    const liveSub = quickActionSubtitle(tile.id);
                    return (
                      <button
                        key={tile.id}
                        onClick={() => ionRouter.push(tile.path, 'forward', 'push')}
                        className={`fhv2-action ${tone}`}
                      >
                        {badge && <span className="fhv2-action-badge">{badge}</span>}
                        <div className="fhv2-action-icon">
                          <Icon name={tile.icon} size={18} color="currentColor" weight="duotone" />
                        </div>
                        <h4>{tile.label}</h4>
                        <p>{liveSub || tile.description}</p>
                      </button>
                    );
                  })}
                </div>

                {/* Mini-actions row */}
                <div className="fhv2-section-head">
                  <h4>Everything else.</h4>
                  <span>Shortcuts to the rest of the house.</span>
                </div>
                <div className="fhv2-mini-actions">
                  {mainTiles.slice(0, 8).map(tile => (
                    <button
                      key={tile.id}
                      onClick={() => ionRouter.push(tile.path, 'forward', 'push')}
                      className="fhv2-mini"
                    >
                      <Icon name={tile.icon} size={20} color="rgba(255,255,255,0.75)" weight="duotone" />
                      <span>{tile.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Right rail */}
              <div className="fhv2-rail">
                <div className="fhv2-rail-card">
                  <h4>Up next <span className="fhv2-rail-more">Today</span></h4>
                  {railEvents.length === 0 ? (
                    <div style={{ color: 'var(--fhv2-muted)', fontSize: 12.5 }}>Nothing queued for today.</div>
                  ) : railEvents.map(ev => (
                    <div className="fhv2-timeline-item" key={ev.id}>
                      <div className="fhv2-time">{ev.start_time ? String(ev.start_time).slice(0, 5) : ''}</div>
                      <div style={{ flex: 1 }}>
                        <div className="fhv2-title">{ev.title}</div>
                        <div className="fhv2-who">
                          <span className="fhv2-chip-dot" style={{ background: ev.colour || '#c5512d' }} />
                          {ev.location || relLabel(ev.start_date, ev.start_time)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {(() => {
                  const todayKey = format(new Date(), 'yyyy-MM-dd');
                  const slotOrder = ['breakfast', 'brunch', 'lunch', 'snack', 'dinner', 'supper'];
                  const todayMeals = (mealPlans || [])
                    .filter((m: any) => m.date === todayKey)
                    .sort((a: any, b: any) => slotOrder.indexOf(a.meal_type) - slotOrder.indexOf(b.meal_type));
                  if (todayMeals.length === 0) return null;
                  return (
                    <div className="fhv2-rail-card">
                      <h4>Meals today <button onClick={() => history.push('/app/kitchen')} className="fhv2-rail-more fhv2-unset">Plan</button></h4>
                      {todayMeals.map((m: any) => (
                        <div key={m.id} className="fhv2-stat-row">
                          <span className="fhv2-k" style={{ textTransform: 'capitalize' }}>{m.meal_type || 'Meal'}</span>
                          <span className="fhv2-v">{m.recipe_title || m.custom_meal || '—'}</span>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {(() => {
                  const mostActiveList = [...(lists || [])]
                    .filter(l => (l.pending_count || 0) > 0)
                    .sort((a, b) => (b.pending_count || 0) - (a.pending_count || 0))[0];
                  if (!mostActiveList) return null;
                  return (
                    <div className="fhv2-rail-card" onClick={() => history.push(`/app/lists/${mostActiveList.id}`)} style={{ cursor: 'pointer' }}>
                      <h4>Shopping <span className="fhv2-rail-more">Open</span></h4>
                      <div className="fhv2-stat-row">
                        <span className="fhv2-k">{mostActiveList.name}</span>
                        <span className="fhv2-v">
                          {mostActiveList.pending_count || 0} item{(mostActiveList.pending_count || 0) === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {isAdmin && (
                  <div className="fhv2-rail-card">
                    <h4>This month <button onClick={() => history.push('/app/budget')} className="fhv2-rail-more fhv2-unset">Budget</button></h4>
                    <div className="fhv2-stat-row"><span className="fhv2-k">Spent this week</span><span className="fhv2-v">£{(weeklyExpenses / 100).toFixed(2)}</span></div>
                    <div className="fhv2-stat-row"><span className="fhv2-k">Open tasks</span><span className="fhv2-v">{pendingTasks.length}</span></div>
                    <div className="fhv2-stat-row"><span className="fhv2-k">Docs updated</span><span className="fhv2-v">{recentDocs.length}</span></div>
                    {tomorrowEvents.length > 0 && (
                      <div className="fhv2-stat-row"><span className="fhv2-k">Tomorrow</span><span className="fhv2-v">{tomorrowEvents.length} event{tomorrowEvents.length === 1 ? '' : 's'}</span></div>
                    )}
                  </div>
                )}

                {family?.members && family.members.length > 0 && (
                  <div className="fhv2-rail-card">
                    <h4>Who's home</h4>
                    {family.members.slice(0, 4).map(m => {
                      // Activity derived from real data rather than role (dashboard-desktop.pdf:
                      // "Kitchen · cooking", "At school", "Away · at Nan's"). Priority:
                      // 1. current known location (place_name), 2. age if set, 3. role fallback.
                      const loc = locations.find(l => l.user_id === m.id);
                      const ageLabel = (m as any).age ? `age ${(m as any).age}` : null;
                      const locLabel = loc?.place_name
                        ? (loc.place_name.toLowerCase().includes('home') ? 'Home' : `At ${loc.place_name}`)
                        : null;
                      const activity = locLabel || ageLabel || (m.role === 'admin' ? 'Admin' : m.role || 'Member');
                      const subtitle = m.role === 'admin' && locLabel ? locLabel : activity;
                      return (
                        <div className="fhv2-member-row" key={m.id}>
                          <div className="fhv2-avatar" style={{ background: m.colour || '#c5512d' }}>
                            {m.avatar
                              ? <img src={m.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                              : initials(m.displayName)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="fhv2-name">
                              {m.displayName}
                              {ageLabel && !m.displayName.includes(ageLabel) ? ` · ${ageLabel}` : ''}
                            </div>
                            <div className="fhv2-status">{subtitle}</div>
                          </div>
                          {locLabel === 'Home' && <div className="fhv2-pulse" />}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Empty family state */}
            {!family && (
              <div className="fhv2-empty">
                <Icon name="group_add" size={56} color="var(--fhv2-muted)" />
                <h4>Set up your family</h4>
                <p>Create or join a family to get started.</p>
                <button onClick={() => history.push('/family-setup')} className="fh-btn-primary" style={{ marginTop: 16 }}>Get Started</button>
              </div>
            )}
          </div>

          {renderFab()}
        </IonContent>
      </IonPage>
    );
  }

  // ─── MOBILE LAYOUT (v2) ───
  const heroMobileSub = upNextEvent
    ? `${format(now, 'EEE d LLL')} · ${String(upNextEvent.start_time || '').slice(0, 5) || format(now, 'HH:mm')}`
    : format(now, 'EEEE, d MMMM · HH:mm');

  return (
    <IonPage>
      <IonContent fullscreen className="fh-mobile-content">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {/* Extra bottom padding on the home tab so the FAB (which sits above
            the bottom nav at `--fh-fab-bottom`) doesn't overlap the last row
            of tiles. Without this, the Reminders tile was falling behind the
            FAB on small viewports. */}
        <div className="fhv2-mobile fh-mobile-home" style={{ paddingBottom: 'calc(var(--fh-fab-bottom) + 56px + 24px)' }}>
          <header className="fhv2-m-header">
            <button
              onClick={() => history.push('/app/profile')}
              className="fhv2-m-brand fhv2-unset"
              aria-label="Profile"
              style={{ gap: 10, display: 'flex', alignItems: 'center' }}
            >
              <span className="fhv2-m-brand-avatar" style={{ background: user?.colour || 'var(--fhv2-terra)' }}>
                {user?.avatar
                  ? <img src={user.avatar} alt="" loading="lazy" />
                  : (user ? initials(user.displayName || user.email) : '?')}
              </span>
              <span className="fhv2-m-brand-text">
                <div className="t">{family?.name || 'FamHearth'}</div>
                <div className="s">
                  {family?.members?.length
                    ? `${family.members.length} member${family.members.length === 1 ? '' : 's'} · all synced`
                    : format(now, 'EEEE, d MMMM')}
                </div>
              </span>
            </button>
            <button
              onClick={() => history.push('/app/more')}
              className="fhv2-m-iconbtn"
              aria-label="Settings"
            >
              <Icon name="settings" size={18} color="rgba(255,255,255,0.7)" />
            </button>
          </header>

          <div className="fhv2-m-hero">
            <h2>{getGreeting()}, <em>{firstName}</em></h2>
            <div className="fhv2-m-hero-date">{heroMobileSub}</div>

            {/* Only show when today has actual tasks — the widget hides
                entirely on quiet days rather than reporting lifetime totals. */}
            {todayProgressTotal > 0 && (
              <>
                <div className="fhv2-m-hero-progress-label">
                  <span>{todayProgressDone} of {todayProgressTotal} today</span>
                  <b>{Math.max(todayProgressTotal - todayProgressDone, 0)} left</b>
                </div>
                <div className="fhv2-progress-track">
                  <div style={{ width: `${todayProgressPct}%` }} />
                </div>
              </>
            )}

            {chipItems.length > 0 && (
              <div className="fhv2-m-hero-chips">
                {chipItems.slice(0, 2).map((c, i) => (
                  <button
                    key={i}
                    className="fhv2-chip fhv2-unset"
                    onClick={() => c.action && history.push(c.action)}
                  >
                    <span className="fhv2-chip-dot" style={{ background: c.color }} />
                    {c.text}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile summary strip — at-a-glance counts so mobile users aren't
              left staring at a blank grid. Mirrors the desktop right-rail stats. */}
          <div className="fh-home-stat-grid">
            {(() => {
              const totalPending = lists.reduce((sum, l) => sum + (Number(l.pending_count) || 0), 0);
              const stats = [
                { label: 'Today', value: todayEvents.length, color: '#b39ddb' },
                { label: 'Tasks', value: pendingTasks.length, color: '#81c784' },
                { label: 'Shop', value: totalPending, color: '#e9c46a' },
                { label: 'Soon', value: upcomingBirthdays.length, color: '#ff8a65' },
              ];
              return stats.map(s => (
                <div key={s.label} className="fh-home-stat-card">
                  <div style={{
                    fontFamily: 'var(--fhv2-serif)', fontStyle: 'italic',
                    fontSize: 22, lineHeight: 1, color: '#fff',
                  }}>{s.value}</div>
                  <div style={{
                    fontFamily: 'var(--fhv2-mono)', fontSize: 9, letterSpacing: '0.08em',
                    textTransform: 'uppercase', color: 'var(--fhv2-muted)', marginTop: 4,
                  }}>{s.label}</div>
                </div>
              ));
            })()}
          </div>

          {pinnedNotes.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <Icon name="sticky_note_2" size={14} color="#ffb74d" />
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' as const, color: 'var(--fhv2-muted)' }}>
                  Pinned notes
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 }}>
                {pinnedNotes.slice(0, 5).map(note => (
                  <button
                    key={note.id}
                    onClick={() => history.push('/app/notes')}
                    className="fhv2-unset"
                    style={{
                      flexShrink: 0, width: 160, padding: '12px 14px',
                      background: 'rgba(255,255,255,0.04)', borderRadius: 14,
                      textAlign: 'left' as const,
                      borderTop: `3px solid ${note.colour || '#ffb74d'}`,
                    }}
                  >
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#fff', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{note.title}</p>
                    {note.content && (
                      <p style={{ fontSize: 11, color: 'var(--fhv2-muted)', margin: '4px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' }}>
                        {note.content}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="fhv2-m-grid">
            {visibleTiles.length === 0 ? (
              <div className="fhv2-empty">
                <Icon name="dashboard_customize" size={40} color="var(--fhv2-muted)" />
                <h4>No shortcuts yet</h4>
                <p>Pick what appears on Home to make this feel like your family dashboard.</p>
                <button onClick={() => history.push('/app/customise-home')} className="fh-btn-primary" style={{ marginTop: 12 }}>Customise home</button>
              </div>
            ) : visibleTiles.map(tile => {
              const badge = tile.id === 'chat' ? '3' : undefined;
              return (
                <button
                  key={tile.id}
                  onClick={() => ionRouter.push(tile.path, 'forward', 'push')}
                  className="fhv2-m-tile fhv2-unset"
                  aria-label={tile.label}
                >
                  {badge && <span className="fhv2-m-tile-badge">{badge}</span>}
                  <div className="fhv2-m-tile-icon" style={{ background: `${tile.color}26`, color: tile.color }}>
                    <Icon name={tile.icon} size={18} color={tile.color} weight="duotone" />
                  </div>
                  <div className="fhv2-m-tile-label">{tile.label}</div>
                </button>
              );
            })}
          </div>

          {!family && (
            <div className="fhv2-empty">
              <Icon name="group_add" size={48} color="var(--fhv2-muted)" />
              <h4>Set up your family</h4>
              <p>Create or join a family to get started.</p>
              <button onClick={() => history.push('/family-setup')} className="fh-btn-primary" style={{ marginTop: 16 }}>Get Started</button>
            </div>
          )}
        </div>

        {renderFab()}
      </IonContent>
    </IonPage>
  );
};

export default HomePage;
